/**
 * Staff screen (DNKADM-0053 U1) — the `v2_staff` entity list + create/edit/
 * deactivate/reactivate (handoff §5.6). `RequireAdmin`-gated route + a Scheduling nav
 * item ("Staff"). Replaces the v1 `lastName == "ServiceAccount"` partition with a
 * first-class staff entity (§9.1/§9.3).
 *
 * Loads `listStaff({ status })` through `useResource`, keyed on the
 * Active/Inactive/All filter (`useActiveFilter` + `<ActiveFilter>`, §4.3). A table of
 * name → email → status; the row/name/chevron click opens the detail (schedule,
 * exceptions, caps) per the DNKADM-0284 row-open pattern, with Deactivate/Reactivate
 * as explicit secondary actions that stop propagation. Base-field editing lives on
 * the detail page (DNKADM-0286 retired the list quick-edit trigger); "Add staff
 * member" opens the {@link StaffDialog} to create. Every write refetches + toasts. A
 * first-run empty tenant (§8) shows a strong empty state with a create CTA.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * name/email search and the Active/Inactive/All filter; Name, Email and Status have shared
 * {@link SortableHeader}s (default Name ascending). Search, filter and sort live in the
 * URL (`staffListView.ts`).
 *
 * **Remove and Show removed (DNKADM-0481 / DNKAPI-0697).** OWNER/ADMIN get a Remove action
 * per row ({@link RemoveStaffDialog}: archive, the upcoming-bookings refusal, the optional
 * login revoke). A "Show removed" switch (URL `removed=1`) swaps the list for the removed
 * staff only, each with Restore — a restored staffer comes back inactive. Removed rows do
 * not open the detail. Remove/Restore are hidden from a STAFF session and from a providerless
 * render (`useOptionalOperator().isAdmin`); dink-api enforces the same.
 *
 * **Not bookable yet (DNKADM-0492 / DNKAPI-0714).** An active row whose `setupHealth` says
 * `bookable: false` carries a "Not bookable yet" chip next to its status; the detail page
 * lists what is missing. No chip when the field is absent.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { AssignedLocationsCell } from '@/components/AssignedLocationsCell';
import { useToast } from '@/components/useToast';
import { serverMessage } from '@/api/serverMessage';
import {
  deactivateStaff,
  listStaff,
  reactivateStaff,
  restoreStaff,
  staffDisplayName,
  type V2Staff,
} from '@/api/staff';
import { useOptionalOperator } from '@/auth/authHooks';
import { getPlanUsage, planLimitFor } from '@/api/planUsage';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import { StaffDialog } from './StaffDialog';
import { RemoveStaffDialog, type RemoveStaffOutcome } from './RemoveStaffDialog';
import { restoreErrorMessage, staffRemovedMessage } from './staffError';
import { showNotBookableChip } from './setupHealth';
import { ApiError } from '@/api/errors';
import {
  STAFF_DEFAULT_SORT,
  STAFF_SORT_KEYS,
  STAFF_URL_DEFAULTS,
  staffMatches,
  staffSortValue,
  type StaffSortKey,
} from './staffListView';
import { ScreenError } from '@/components/ScreenError';

export function StaffScreen() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  const navigate = useNavigate();
  // DNKADM-0430: search, status filter and sort come from the URL (`staffListView.ts`).
  const url = useListUrlState(STAFF_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, STAFF_SORT_KEYS, STAFF_DEFAULT_SORT);
  // DNKADM-0481: the removed (archived) staff, instead of the live list.
  const showRemoved = url.values.removed === '1';
  // Remove/Restore are OWNER/ADMIN (SET UP); a STAFF session or a providerless render sees neither.
  const canManage = useOptionalOperator()?.isAdmin === true;

  const staff = useResource(
    useCallback(
      () => listStaff({ status: filter.status, removed: showRemoved }),
      [filter.status, showRemoved],
    ),
    [filter.status, showRemoved],
  );

  // Plan usage drives the button-level Add gate (DNKADM-0069); loaded once and
  // refetched after each add/delete (a slot changes). Degrades to the submit-time
  // gate (DNKADM-0065) when it can't load — `planLimitFor` returns null on error.
  const planUsage = useResource(getPlanUsage);

  // Dialog state: `null` = closed; `{ staff: null }` = add; `{ staff }` = edit.
  const [dialog, setDialog] = useState<{ staff: V2Staff | null } | null>(null);
  // The plan-limit popup payload when Add is gated at the button; null = closed.
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);
  // The staffer the Remove dialog is open for; null = closed.
  const [removeTarget, setRemoveTarget] = useState<V2Staff | null>(null);
  // The removed row whose Restore is in flight (one request per click); null = none.
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const refetch = staff.refetch;

  const visibleRows = useMemo(
    () =>
      sortRows(
        (staff.data ?? []).filter((row) => staffMatches(row, search)),
        (row) => staffSortValue(row, sort.key),
        sort.dir,
        (row) => row.id,
      ),
    [staff.data, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: StaffSortKey) => setUrl(toSortParams(toggleSort(sort, key), STAFF_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const refetchUsage = planUsage.refetch;

  // Add: when the axis is at its plan cap, open the upgrade popup directly (the
  // form never opens); otherwise open the create form exactly as before. An
  // unknown/unlimited/unavailable usage never gates (`planLimitFor` → null).
  const handleAdd = useCallback(() => {
    const info = planLimitFor(planUsage.data, 'staff');
    if (info) {
      setPlanLimit(info);
      return;
    }
    setDialog({ staff: null });
  }, [planUsage.data]);

  const handleDeactivate = useCallback(
    async (member: V2Staff) => {
      try {
        await deactivateStaff(member.id);
        refetch();
        refetchUsage();
        showToast('Staff member deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the staff member.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  const handleReactivate = useCallback(
    async (member: V2Staff) => {
      try {
        await reactivateStaff(member.id);
        refetch();
        refetchUsage();
        showToast('Staff member reactivated', 'success');
      } catch (err) {
        // Reactivate consumes a plan slot (counts are of ACTIVE records), so it is
        // plan-limit enforced exactly like Add (DNKADM-0065 / DNKAPI-0139) — mirror
        // the create flow's branching, keyed on the body `error` code (not status).
        // F-291: removed elsewhere since the list loaded — point to Restore, refresh the list.
        const removedMessage = staffRemovedMessage(err, staffDisplayName(member));
        if (removedMessage !== null) {
          refetch();
          showToast(removedMessage, 'error');
          return;
        }
        const code = v2ErrorCode(err);
        if (code === 'PLAN_LIMIT_REACHED') {
          // The cap was hit — open the upgrade popup (NOT a generic toast); the
          // record was not reactivated.
          setPlanLimit(parsePlanLimit(err));
          return;
        }
        if (code === 'PLAN_VERIFY_FAILED') {
          // Transient plan-service outage — a retry toast (NOT the popup).
          showToast(
            'We couldn’t verify your plan just now. Please try again in a moment.',
            'error',
          );
          return;
        }
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the staff member.', 'error');
      }
    },
    [refetch, refetchUsage, showToast],
  );

  const handleRemoved = useCallback(
    (member: V2Staff, outcome: RemoveStaffOutcome) => {
      setRemoveTarget(null);
      refetch();
      refetchUsage();
      const name = staffDisplayName(member);
      showToast(
        outcome === 'removed-and-login-off'
          ? `${name} removed, and their login turned off.`
          : outcome === 'already-removed'
            ? `${name} was already removed.`
            : `${name} removed.`,
        'success',
      );
    },
    [refetch, refetchUsage, showToast],
  );

  const handleRestore = useCallback(
    async (member: V2Staff) => {
      const name = staffDisplayName(member);
      setRestoringId(member.id);
      try {
        await restoreStaff(member.id);
        refetch();
        refetchUsage();
        showToast(`${name} restored as inactive. Reactivate them to make them bookable.`, 'success');
      } catch (err) {
        const message = restoreErrorMessage(err, name);
        // A 404: they are no longer removed (restored elsewhere) — refresh so the row goes.
        if (err instanceof ApiError && err.status === 404) refetch();
        showToast(message, 'error');
      } finally {
        setRestoringId(null);
      }
    },
    [refetch, refetchUsage, showToast],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" component="h1">
            Staff
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            The people who deliver services. Assign each to locations, then set their weekly
            schedule, date exceptions, and appointment caps.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={handleAdd}
          sx={{ flexShrink: 0 }}
        >
          Add staff member
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search staff',
          disabled: staff.status !== 'ready',
        }}
        filters={
          <>
            {!showRemoved && (
              <ActiveFilter
                value={filter.value}
                onChange={filter.setValue}
                aria-label="Filter staff by status"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showRemoved}
                  onChange={(e) => setUrl({ removed: e.target.checked ? '1' : null })}
                  slotProps={{ input: { 'aria-label': 'Show removed' } }}
                />
              }
              label="Show removed"
              sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 12.5 } }}
            />
          </>
        }
        onClear={() => setUrl({ q: null, status: null, removed: null })}
        canClear={search.length > 0 || filter.value !== 'active' || showRemoved}
      />

      {staff.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={220}
          role="status"
          aria-label="Loading staff"
          sx={{ borderRadius: 3 }}
        />
      )}

      {staff.status === 'error' && (
        <ScreenError
          error={staff.error}
          what="the staff"
          onRetry={staff.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {staff.status === 'ready' && staff.data && staff.data.length === 0 && (
        // First-run empty state (§8) with a strong create CTA.
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="badge" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {showRemoved
              ? 'No removed staff'
              : filter.value === 'active'
                ? 'No staff yet'
                : 'No staff match this filter'}
          </Typography>
          {!showRemoved && filter.value === 'active' && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                Add your first staff member, assign them to locations, and set their schedule to
                start offering bookable services.
              </Typography>
              <Button variant="contained" startIcon={<Icon name="add" />} onClick={handleAdd}>
                Add staff member
              </Button>
            </>
          )}
        </Paper>
      )}

      {staff.status === 'ready' && staff.data && staff.data.length > 0 && (
        <TableContainer>
          <Table aria-label="Staff" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                <SortableHeader label="Email" col="email" sort={sort} onSort={onSort} />
                <TableCell>Locations</TableCell>
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
                <TableCell sx={{ width: 44 }} aria-hidden />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((member) => {
                const name = staffDisplayName(member);
                if (showRemoved) {
                  // A removed row: no detail to open; Restore is its one action.
                  return (
                    <TableRow key={member.id}>
                      <TableCell>{name}</TableCell>
                      <TableCell>{member.email ?? '—'}</TableCell>
                      <TableCell>
                        <AssignedLocationsCell locations={member.locations} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label="Removed" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        {canManage && (
                          <Button
                            size="small"
                            aria-label={`Restore staff ${name}`}
                            disabled={restoringId === member.id}
                            onClick={() => void handleRestore(member)}
                          >
                            Restore
                          </Button>
                        )}
                      </TableCell>
                      <TableCell aria-hidden />
                    </TableRow>
                  );
                }
                return (
                  <TableRow
                    key={member.id}
                    hover
                    onClick={() => navigate(`/staff/${member.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={`/staff/${member.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {name}
                      </MuiLink>
                    </TableCell>
                    <TableCell>{member.email ?? '—'}</TableCell>
                    <TableCell>
                      <AssignedLocationsCell locations={member.locations} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          label={member.isActive ? 'Active' : 'Inactive'}
                          color={member.isActive ? 'success' : 'default'}
                          variant={member.isActive ? 'filled' : 'outlined'}
                        />
                        {showNotBookableChip(member) && (
                          <Chip
                            size="small"
                            label="Not bookable yet"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {member.isActive ? (
                        <IconAction
                          size="small"
                          label={`Deactivate staff ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDeactivate(member);
                          }}
                        >
                          <Icon name="toggle_off" />
                        </IconAction>
                      ) : (
                        <IconAction
                          size="small"
                          label={`Reactivate staff ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleReactivate(member);
                          }}
                        >
                          <Icon name="toggle_on" />
                        </IconAction>
                      )}
                      {canManage && (
                        <IconAction
                          size="small"
                          label={`Remove staff ${name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setRemoveTarget(member);
                          }}
                        >
                          <Icon name="delete" />
                        </IconAction>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ color: t.textGhost }}>
                      <IconAction
                        size="small"
                        label={`Open ${name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/staff/${member.id}`);
                        }}
                      >
                        <Icon name="chevron_right" />
                      </IconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {staff.status === 'ready' &&
        staff.data &&
        staff.data.length > 0 &&
        visibleRows.length === 0 && <ListNoMatch noun="staff" query={search} />}

      <StaffDialog
        open={dialog !== null}
        staff={dialog?.staff ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
          refetchUsage();
        }}
        showToast={showToast}
      />

      {removeTarget !== null && (
        <RemoveStaffDialog
          key={removeTarget.id}
          staff={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemoved={(outcome) => handleRemoved(removeTarget, outcome)}
          onRemovedLoginStillActive={() => {
            refetch();
            refetchUsage();
          }}
        />
      )}

      <PlanLimitDialog
        open={planLimit !== null}
        info={planLimit}
        onClose={() => setPlanLimit(null)}
      />
    </Box>
  );
}
